/**
 * The API component is a wrapper to ease the use of AJAX calls.
 */

// Import statements
import {useEffect, useReducer} from "react";
import {reducer} from "./reducer";
import {requestFailed, requestStarted, requestSuccessful} from "./constants";

/**
 * Make an GET API call
 * @param url
 * @returns {S}
 */
export const useGet = ({url}) => {

    const [state, dispatch] = useReducer(reducer, {
        isLoading: true,
        data: null,
        error: null,
    });

    useEffect(() => {

        const abortController = new AbortController();

        const fetchData = async () => {
            dispatch(requestStarted());

            try {
                const response = await fetch(url, {signal: abortController.signal});

                if (!response.ok) {
                    throw new Error(
                        `${response.status} ${response.statusText}`
                    );
                }

                const data = await response.json();

                dispatch(requestSuccessful({data}));
            } catch (e) {

                // only call dispatch when we know the fetch was not aborted
                if (!abortController.signal.aborted) {
                    dispatch(requestFailed({error: e.message}));
                }
            }
        };

        fetchData();

        return () => {
            abortController.abort();
        };

    }, [url]);

    return state;
};

/**
 * Make an POST API call
 * @param url
 * @param formData
 * @returns {S}
 */
export const usePost = ({url, formData}) => {

    const [state, dispatch] = useReducer(reducer, {
        isLoading: true,
        data: null,
        error: null,
    });

    useEffect(() => {

        const abortController = new AbortController();

        const fetchData = async () => {
            dispatch(requestStarted());

            try {
                const response = await fetch(url, {
                    signal: abortController.signal,
                    method: 'POST',
                    credentials: 'same-origin',
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(
                        `${response.status} ${response.statusText}`
                    );
                }

                const data = await response.json();

                dispatch(requestSuccessful({data}));
            } catch (e) {

                // only call dispatch when we know the fetch was not aborted
                if (!abortController.signal.aborted) {
                    dispatch(requestFailed({error: e.message}));
                }
            }
        };

        fetchData();

        return () => {
            abortController.abort();
        };

    }, [url]) // eslint-disable-line react-hooks/exhaustive-deps

    return state;
};

/**
 * Make AJAX call
 * @param host
 * @param data
 */
export function postCall(host, data) {
    try {
        return fetch(host, {
            method: 'POST',
            credentials: 'same-origin',
            body: data
        })
    } catch (error) {
        console.log("error", error);
    }
}